/**
 * Waiting screen.
 *
 * Reads /api/tasks/waiting (status in waiting/delegated). Buckets:
 *   - Stale (review_at < today - 7d, or no review_at + waiting_since > 7d)
 *   - Due today (review_at within today)
 *   - Upcoming (review_at > today)
 */

import { useTasksWaiting } from '../api/tasks';
import type { TaskDto } from '../api/tasks';
import { TaskSection } from '../components/TaskSection';
import { useSectionSorts } from '../lib/task-sort';
import { useToggleTask } from './_use-open-task';
import { useTaskQuickActions } from './_use-task-quick-actions';
import styles from './Lists.module.css';

const STALE_THRESHOLD_DAYS = 7;

export function WaitingScreen() {
  const query = useTasksWaiting();
  const complete = useToggleTask();
  const quick = useTaskQuickActions();
  // Must be called before the early isLoading/isError returns below so React
  // sees a stable hook order on every render.
  const sorts = useSectionSorts({
    'waiting.stale': 'priority',
    'waiting.dueToday': 'priority',
    'waiting.upcoming': 'priority',
  });

  if (query.isLoading) return <div className={styles.loading}>Loading…</div>;
  if (query.isError) return <div className={styles.error}>Couldn’t load waiting.</div>;

  const tasks = query.data?.tasks ?? [];
  const { stale, dueToday, upcoming } = bucket(tasks);
  const total = tasks.length;

  return (
    <>
      <div className={styles.head}>
        <h2 className={styles.title}>Waiting · Follow-ups</h2>
        <div className={styles.sub}>
          {total} open · {stale.length} stale · {dueToday.length} due today
        </div>
      </div>

      <TaskSection
        title="Stale"
        alert={stale.length > 0 ? 'needs nudge' : null}
        tasks={stale}
        showStatus
        rowOptions={{ showWaitingSince: true }}
        sort={sorts.sortFor('waiting.stale')}
        onSortChange={(next) => sorts.setSortFor('waiting.stale', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Due today"
        tasks={dueToday}
        showStatus
        rowOptions={{ showWaitingSince: true }}
        sort={sorts.sortFor('waiting.dueToday')}
        onSortChange={(next) => sorts.setSortFor('waiting.dueToday', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Upcoming"
        tasks={upcoming}
        showStatus
        rowOptions={{ showWaitingSince: true }}
        sort={sorts.sortFor('waiting.upcoming')}
        onSortChange={(next) => sorts.setSortFor('waiting.upcoming', next)}
        onComplete={complete}
        quick={quick}
      />

      {total === 0 && <div className={styles.empty}>No open follow-ups.</div>}
    </>
  );
}

function bucket(tasks: TaskDto[]) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const staleCutoff = start.getTime() - STALE_THRESHOLD_DAYS * 86_400_000;

  const stale: TaskDto[] = [];
  const dueToday: TaskDto[] = [];
  const upcoming: TaskDto[] = [];

  for (const t of tasks) {
    const review = t.reviewAt ? Date.parse(t.reviewAt) : null;
    if (review === null) {
      // No review_at — fall back to created_at age for stale check.
      const createdMs = Date.parse(t.createdAt);
      if (createdMs < staleCutoff) stale.push(t);
      else upcoming.push(t);
      continue;
    }
    if (review < staleCutoff) stale.push(t);
    else if (review <= end.getTime()) dueToday.push(t);
    else upcoming.push(t);
  }
  return { stale, dueToday, upcoming };
}
